import React from "react";
import { Box, SimpleGrid, Spinner, Flex } from "@chakra-ui/react";
import { VideoCard } from "./VideoCard";
import { useEffect, useRef, useMemo } from "react";

export const VideoGrid = ({
  currentVideos,
  isLoading,
  isLoadingQuery,
  onVideoClick,
  getVideoId,
  onLoadMore,
  hasMore,
  customSegments = {},
}) => {
  const containerRef = useRef(null);

  // Memoize the video cards to prevent unnecessary re-renders
  const videoCards = useMemo(() => {
    return currentVideos.map((video) => {
      const videoId = getVideoId(video);
      return (
        <VideoCard
          confidenceCounts={video.confidenceCounts}
          customSegments={customSegments[videoId] || []}
          key={videoId}
          onVideoClick={onVideoClick}
          videoId={videoId}
        />
      );
    });
  }, [currentVideos, getVideoId, onVideoClick, customSegments]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        onLoadMore();
      }
    }, options);

    const spinnerElement = document.querySelector("#infinite-scroll-spinner");
    if (spinnerElement) {
      observer.observe(spinnerElement);
    }

    return () => {
      if (spinnerElement) {
        observer.unobserve(spinnerElement);
      }
    };
  }, [onLoadMore]);

  // Initial loading state with skeleton
  if (isLoading && currentVideos.length === 0) {
    return (
      <SimpleGrid
        columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5, "2xl": 6 }}
        px={4}
        spacing={{ base: 4, md: 6 }}
      >
        {[...Array(12)].map((_, i) => (
          <Box
            animation="pulse 1.5s infinite"
            bg="gray.100"
            borderRadius="md"
            height="200px"
            key={i}
          />
        ))}
      </SimpleGrid>
    );
  }

  const showSpinner = hasMore && !isLoadingQuery;

  return (
    <Box
      h="100%"
      overflowY="auto"
      pb={8}
      position="relative"
      px={4}
      ref={containerRef}
    >
      <SimpleGrid
        columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5, "2xl": 6 }}
        mb={4}
        spacing={{ base: 4, md: 6 }}
      >
        {videoCards}
      </SimpleGrid>
      {!!showSpinner && (
        <Flex id="infinite-scroll-spinner" justify="center" py={4}>
          <Spinner size="xl" />
        </Flex>
      )}
    </Box>
  );
};
